/**
 * Pure helpers for cash forecast / money-on-table SVG charts.
 * Exact cents in labels; axis never uses $K/$M abbreviations.
 */
import type {
  AriaCashForecastDigest,
  AriaCashForecastWeek,
  AriaMoneyOnTableLine,
} from '../../api/ariaChat'
import { formatWeekLabel, isShortWeek } from './cashForecastCardModel'

export type ChartWeekBar = {
  week_start: string
  weekLabel: string
  inAmount: number
  subsOut: number
  payroll: number
  overhead: number
  otherOut: number
  closeAmount: number
  closeFormatted: string
  inFormatted: string
  isShort: boolean
  isLowest: boolean
}

export type MotBarRow = {
  kind: string
  label: string
  amount: number
  formatted: string
  hatched: boolean
  href?: string | null
}

/** Axis / display: full dollars, never abbreviated (e.g. $10,000). */
export function formatAxisMoney(amount: number): string {
  const n = Math.round(amount)
  const abs = Math.abs(n)
  const body = abs.toLocaleString('en-US', {
    maximumFractionDigits: 0,
    minimumFractionDigits: 0,
  })
  return n < 0 ? `-$${body}` : `$${body}`
}

/** Hover/tap: exact cents (matches table money.formatted when available). */
export function formatExactMoney(
  amount: number,
  formatted?: string | null,
): string {
  if (formatted) return formatted
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount)
}

export function moneyAmount(m?: { amount?: number } | null): number {
  return m?.amount != null && Number.isFinite(m.amount) ? m.amount : 0
}

export function buildForecastBars(
  weeks: AriaCashForecastWeek[],
  verdict: AriaCashForecastDigest['verdict'],
): ChartWeekBar[] {
  if (!weeks.length) return []
  let lowestIdx = 0
  let lowestVal = moneyAmount(weeks[0]?.close)
  weeks.forEach((w, i) => {
    const c = moneyAmount(w.close)
    if (c < lowestVal) {
      lowestVal = c
      lowestIdx = i
    }
  })
  if (verdict?.lowest_week) {
    const idx = weeks.findIndex((w) => w.week_start === verdict.lowest_week)
    if (idx >= 0) lowestIdx = idx
  }
  return weeks.map((w, i) => ({
    week_start: w.week_start,
    weekLabel: formatWeekLabel(w.week_start),
    inAmount: moneyAmount(w.in),
    subsOut: moneyAmount(w.subs_out),
    payroll: moneyAmount(w.payroll),
    overhead: moneyAmount(w.overhead),
    otherOut: moneyAmount(w.other_out),
    closeAmount: moneyAmount(w.close),
    closeFormatted: formatExactMoney(moneyAmount(w.close), w.close?.formatted),
    inFormatted: formatExactMoney(moneyAmount(w.in), w.in?.formatted),
    isShort: isShortWeek(w.week_start, verdict),
    isLowest: i === lowestIdx,
  }))
}

export function chartDomain(bars: ChartWeekBar[]): { min: number; max: number } {
  if (!bars.length) return { min: 0, max: 1 }
  let max = 0
  let min = 0
  for (const b of bars) {
    max = Math.max(max, b.inAmount, b.closeAmount)
    const outStack = b.subsOut + b.payroll + b.overhead + b.otherOut
    min = Math.min(min, -outStack, b.closeAmount)
  }
  if (max === min) {
    max = min + 1
  }
  const pad = Math.max(Math.abs(max), Math.abs(min)) * 0.08
  return { min: min - pad, max: max + pad }
}

/** Nice axis ticks without abbreviations — raw dollar amounts. */
export function axisTicks(min: number, max: number, count = 3): number[] {
  if (!(max > min)) return [0]
  const step = (max - min) / Math.max(1, count - 1)
  const ticks: number[] = []
  for (let i = 0; i < count; i++) {
    ticks.push(min + step * i)
  }
  return ticks
}

export function buildMotBars(
  lines: AriaMoneyOnTableLine[],
  estimate?: AriaMoneyOnTableLine | null,
): MotBarRow[] {
  const rows: MotBarRow[] = lines
    .filter((l) => moneyAmount(l.amount) > 0)
    .map((l) => ({
      kind: l.kind || 'fact',
      label: l.label || 'Line',
      amount: moneyAmount(l.amount),
      formatted: formatExactMoney(moneyAmount(l.amount), l.amount?.formatted),
      hatched: false,
      href: l.href,
    }))
  if (estimate && moneyAmount(estimate.amount) > 0) {
    rows.push({
      kind: estimate.kind || 'owner_estimate',
      label: estimate.label || 'Your estimate',
      amount: moneyAmount(estimate.amount),
      formatted: formatExactMoney(
        moneyAmount(estimate.amount),
        estimate.amount?.formatted,
      ),
      hatched: true,
      href: estimate.href,
    })
  }
  return rows
}

export function motMaxAmount(rows: MotBarRow[]): number {
  return Math.max(1, ...rows.map((r) => r.amount))
}

/** Persistent chart readout: "Oct 8: $89,825.34". */
export function weekCloseReadout(
  weekStart: string,
  closeAmount: number,
  formatted?: string | null,
): string {
  return `${formatWeekLabel(weekStart)}: ${formatExactMoney(closeAmount, formatted)}`
}

/** Screen-reader summary matching table close figures. */
export function forecastChartAltText(bars: ChartWeekBar[]): string {
  if (!bars.length) return 'Cash forecast chart with no weeks.'
  const parts = bars.map((b) => {
    const short = b.isShort ? ' (first short week)' : ''
    const low = b.isLowest ? ' (lowest close)' : ''
    return `${b.weekLabel}: in ${b.inFormatted}, close ${b.closeFormatted}${short}${low}`
  })
  return `Cash forecast by week. ${parts.join('. ')}.`
}

export function sparklineAltText(
  closes: { weekLabel: string; closeFormatted: string }[],
): string {
  if (!closes.length) return 'Projected balance sparkline unavailable.'
  return `Projected closing balance over ${closes.length} weeks: ${closes
    .map((c) => `${c.weekLabel} ${c.closeFormatted}`)
    .join(', ')}.`
}

/** Assert helper: axis string must never contain K/M abbreviations. */
export function axisHasNoAbbreviation(label: string): boolean {
  return !/\$[\d,.]*\s*[KMB]\b/i.test(label) && !/\dK\b/i.test(label)
}
